/**
 * RedMart header behaviour: dispatch countdown, dialogs (menu, search, cart),
 * the desktop mega menu, predictive search and the AJAX cart.
 * Progressive enhancement: without JS the search form submits to /search and
 * the cart button's fallback link (/cart) still works.
 */

const ROOT = window.Shopify?.routes?.root ?? '/';
const CART_SECTION = 'rm-cart-drawer';

/* ---------- Dispatch countdown (IST) ---------- */

class RmCountdown extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.clock = this.querySelector('[data-clock]');
    this.before = this.querySelector('[data-before]');
    this.after = this.querySelector('[data-after]');
    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  #tick() {
    const cutoff = Number(this.dataset.cutoffHour);
    // India has no daylight saving: IST is always UTC+5:30.
    const ist = new Date(Date.now() + 330 * 60 * 1000);
    const secondsNow = ist.getUTCHours() * 3600 + ist.getUTCMinutes() * 60 + ist.getUTCSeconds();
    const left = cutoff * 3600 - secondsNow;
    const open = left > 0;

    this.before.hidden = !open;
    this.after.hidden = open;
    if (!open) return;

    const pad = (n) => String(n).padStart(2, '0');
    this.clock.textContent = `${pad(Math.floor(left / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;
  }
}

/* ---------- Dialogs: [data-rm-open="<dialog id>"] opens, [data-rm-close] closes ---------- */

/** @param {string} id */
function openDialog(id) {
  const dialog = document.getElementById(id);
  if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
  closeMega();
  for (const panel of document.querySelectorAll('.rm-search__panel')) panel.hidden = true;
  dialog.showModal();
  dialog.querySelector('[data-rm-search-input]')?.focus();
}

document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);

  const opener = target.closest('[data-rm-open]');
  if (opener) {
    event.preventDefault();
    openDialog(opener.getAttribute('data-rm-open'));
    return;
  }

  const closer = target.closest('[data-rm-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }

  // Click on the backdrop (the dialog element itself, outside its content).
  if (target instanceof HTMLDialogElement && target.classList.contains('rm-modal')) {
    target.close();
  }
});

/* ---------- Mega menu (desktop disclosure) ---------- */

/** @param {boolean} visible */
function setMegaScrim(visible) {
  const scrim = document.getElementById('RmMegaScrim');
  if (scrim) scrim.hidden = !visible;
}

function closeMega() {
  const toggle = document.querySelector('[data-rm-mega-toggle]');
  const panel = document.getElementById('RmMega');
  if (!toggle || !panel || panel.hidden) return;
  panel.hidden = true;
  setMegaScrim(false);
  toggle.setAttribute('aria-expanded', 'false');
}

document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const toggle = target.closest('[data-rm-mega-toggle]');
  const panel = document.getElementById('RmMega');
  if (!panel) return;

  if (toggle) {
    const open = panel.hidden;
    panel.hidden = !open;
    setMegaScrim(open);
    toggle.setAttribute('aria-expanded', String(open));
    return;
  }
  if (!panel.hidden && !target.closest('#RmMega')) closeMega();
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const panel = document.getElementById('RmMega');
  if (panel && !panel.hidden) {
    closeMega();
    document.querySelector('[data-rm-mega-toggle]')?.focus();
  }
});

/* ---------- Predictive search ---------- */

class RmSearch extends HTMLElement {
  #controller = null;
  #debounce = 0;
  #initial = '';

  connectedCallback() {
    this.input = this.querySelector('[data-rm-search-input]');
    this.target = this.querySelector('[data-rm-results-target]');
    this.panel = this.querySelector('.rm-search__panel');
    this.#initial = this.target?.innerHTML ?? '';

    this.input?.addEventListener('input', () => {
      window.clearTimeout(this.#debounce);
      this.#debounce = window.setTimeout(() => this.#search(), 200);
    });
    this.input?.addEventListener('focus', () => this.#show());

    this.addEventListener('click', (event) => {
      const chip = /** @type {HTMLElement} */ (event.target).closest('[data-rm-term]');
      if (!chip || !this.input) return;
      this.input.value = chip.getAttribute('data-rm-term') ?? '';
      this.input.focus();
      this.#search();
    });

    this.onDocumentClick = (event) => {
      if (this.panel && !this.contains(/** @type {Node} */ (event.target))) this.#hide();
    };
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.panel && !this.panel.hidden) {
        this.#hide();
        this.input?.focus();
      }
    };
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  #show() {
    if (!this.panel) return;
    closeMega();
    this.panel.hidden = false;
    this.input?.setAttribute('aria-expanded', 'true');
  }

  #hide() {
    if (!this.panel) return;
    this.panel.hidden = true;
    this.input?.setAttribute('aria-expanded', 'false');
  }

  async #search() {
    const terms = this.input?.value.trim() ?? '';
    this.#show();
    if (!terms) {
      this.target.innerHTML = this.#initial;
      return;
    }

    this.#controller?.abort();
    this.#controller = new AbortController();
    const url = new URL(`${ROOT}search/suggest`, window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('section_id', this.dataset.sectionId ?? 'rm-predictive-search');
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '5');

    try {
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const results = html.querySelector('[data-rm-results]');
      if (results) this.target.replaceChildren(results);
    } catch (error) {
      if (error.name !== 'AbortError') console.error('[RedMart] search failed', error);
    }
  }
}

/* ---------- Cart ---------- */

class RmCartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');

    this.addEventListener('click', (event) => {
      const button = /** @type {HTMLElement} */ (event.target).closest('[data-rm-line]');
      if (!button) return;
      this.change(Number(button.getAttribute('data-rm-line')), Number(button.getAttribute('data-rm-qty')));
    });

    // Horizon's own product forms (until RedMart ones replace them).
    this.onHorizonUpdate = async (event) => {
      const promise = event.promise ?? event.detail?.promise;
      try {
        if (promise) await promise;
      } catch {
        return;
      }
      await this.refresh();
      this.open();
    };
    document.addEventListener('shopify:cart:lines-update', this.onHorizonUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this.onHorizonUpdate);
  }

  open() {
    openDialog('RmCart');
  }

  /** @param {string} html */
  render(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-rm-cart-content]');
    const current = this.querySelector('[data-rm-cart-content]');
    if (!fresh || !current) return;
    current.replaceWith(fresh);

    const count = fresh.getAttribute('data-count') ?? '0';
    for (const badge of document.querySelectorAll('[data-rm-cart-count]')) badge.textContent = count;
    for (const button of document.querySelectorAll('[data-rm-cart-button]')) {
      button.setAttribute('aria-label', `Cart, ${count} items`);
    }
    document.dispatchEvent(new CustomEvent('rm:cart-updated', { detail: { count: Number(count) } }));
  }

  async refresh() {
    const response = await fetch(`${ROOT}?sections=${CART_SECTION}`);
    if (!response.ok) return;
    const sections = await response.json();
    this.render(sections[CART_SECTION]);
  }

  /**
   * @param {number} line - 1-based cart line
   * @param {number} quantity
   */
  async change(line, quantity) {
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${ROOT}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: CART_SECTION }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      this.render(data.sections[CART_SECTION]);
    } catch (error) {
      console.error('[RedMart] cart change failed', error);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {FormData} formData
   * @returns {Promise<boolean>}
   */
  async add(formData) {
    formData.append('sections', CART_SECTION);
    const response = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) {
      console.error('[RedMart] add to cart failed', data.description ?? data.message);
      return false;
    }
    this.render(data.sections[CART_SECTION]);
    return true;
  }
}

/* ---------- Quick-add sheet: [data-rm-quick-open="<product url>"] ---------- */

document.addEventListener('click', async (event) => {
  const opener = /** @type {HTMLElement} */ (event.target).closest('[data-rm-quick-open]');
  if (!opener) return;
  const target = document.querySelector('[data-rm-quick-target]');
  if (!target) return;

  event.preventDefault();
  opener.setAttribute('aria-busy', 'true');
  try {
    const url = new URL(opener.getAttribute('data-rm-quick-open'), window.location.origin);
    url.searchParams.set('section_id', 'rm-quick-add');
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const content = html.querySelector('[data-rm-quick-content]');
    if (!content) return;
    target.replaceChildren(content);
    openDialog('RmQuickAdd');
  } catch (error) {
    console.error('[RedMart] quick add failed', error);
    window.location.href = opener.getAttribute('data-rm-quick-open');
  } finally {
    opener.removeAttribute('aria-busy');
  }
});

// Quick-add stepper. The product page's stepper (inside <rm-product>) has its
// own handler in rm-product.js; handling it here too added 1 extra per tap.
document.addEventListener('click', (event) => {
  const step = /** @type {HTMLElement} */ (event.target).closest('[data-rm-step]');
  if (!step || step.closest('rm-product')) return;
  const form = step.closest('form');
  const input = form?.querySelector('input[name="quantity"]');
  if (!(input instanceof HTMLInputElement)) return;

  const next = Math.min(Number(input.max) || 10, Math.max(1, Number(input.value) + Number(step.getAttribute('data-rm-step'))));
  input.value = String(next);
  const button = form.querySelector('[data-rm-totals]');
  const total = button?.querySelector('[data-rm-total]');
  const totals = button?.getAttribute('data-rm-totals')?.split('|') ?? [];
  if (total && totals[next - 1]) total.textContent = totals[next - 1];
});

// Another variant picked: the precomputed totals no longer apply.
document.addEventListener('change', (event) => {
  const select = /** @type {HTMLElement} */ (event.target);
  if (!select.matches('.rm-quick__select')) return;
  const button = select.closest('form')?.querySelector('[data-rm-totals]');
  if (button) button.textContent = 'Add to cart';
});

/* Quick add: any form[data-rm-add]. data-rm-stay = mark "Added" instead of opening the cart. */
document.addEventListener('submit', async (event) => {
  const form = /** @type {HTMLFormElement} */ (event.target);
  if (!form.matches('[data-rm-add]')) return;
  // "Buy now" posts natively with return_to=/checkout.
  if (/** @type {SubmitEvent} */ (event).submitter?.hasAttribute('data-rm-buy-now')) return;
  const drawer = document.querySelector('rm-cart-drawer');
  if (!(drawer instanceof RmCartDrawer)) return;

  event.preventDefault();
  const button = form.querySelector('[type="submit"]');
  button?.setAttribute('disabled', '');
  const ok = await drawer.add(new FormData(form));
  button?.removeAttribute('disabled');
  if (!ok) return;

  if (form.hasAttribute('data-rm-stay')) {
    const added = form.querySelector('.rm-search-row__added');
    if (button instanceof HTMLElement && added instanceof HTMLElement) {
      button.hidden = true;
      added.hidden = false;
    }
  } else {
    const host = form.closest('dialog');
    if (host && host.id !== 'RmCart') host.close();
    drawer.open();
  }
});

if (!customElements.get('rm-countdown')) customElements.define('rm-countdown', RmCountdown);
if (!customElements.get('rm-search')) customElements.define('rm-search', RmSearch);
if (!customElements.get('rm-cart-drawer')) customElements.define('rm-cart-drawer', RmCartDrawer);

/* ---------- Sticky header: hides on scroll down, reappears on scroll up ----------
 * Horizon scrolls .page-wrapper (not the window) at >=990px and the document
 * below that, so scroll position has to be read from whichever one is active.
 */

(() => {
  const bar = document.querySelector('.rm-header__bar[data-sticky="scroll-up"]');
  if (!bar) return;

  const pageWrapper = document.querySelector('.page-wrapper');
  const getScrollY = () => window.scrollY + (pageWrapper ? pageWrapper.scrollTop : 0);

  let lastY = getScrollY();
  let ticking = false;

  const update = () => {
    const y = getScrollY();
    const goingDown = y > lastY;
    const pastThreshold = y > bar.offsetHeight;
    bar.toggleAttribute('data-sticky-hidden', goingDown && pastThreshold);
    lastY = y;
    ticking = false;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  pageWrapper?.addEventListener('scroll', onScroll, { passive: true });
})();

/* ---------- Sticky header offset ----------
 * #header-group is the sticky element; everything above the RedMart header in it
 * (the dispatch strip) should scroll away, so its height becomes a negative top.
 */

(() => {
  const group = document.getElementById('header-group');
  const header = group?.querySelector('rm-header');
  if (!group || !header) return;

  const bar = header.querySelector('.rm-header__bar');
  const sticky = bar?.getAttribute('data-sticky') === 'always';

  const measure = () => {
    const offset = header.getBoundingClientRect().top - group.getBoundingClientRect().top;
    const root = document.documentElement.style;
    root.setProperty('--rm-strip-height', `${Math.max(0, Math.round(offset))}px`);
    // Other sticky bars (product in-page nav) sit just below an always-sticky header.
    root.setProperty('--rm-sticky-header-height', sticky && bar ? `${Math.round(bar.getBoundingClientRect().height)}px` : '0px');
  };

  measure();
  new ResizeObserver(measure).observe(group);
})();
